/**
 * Il marchio DiscoveryGuard, dalla geometria ufficiale in `brand/logo/`.
 *
 * La geometria è COPIATA da lì, non ridisegnata: due disegni «quasi uguali»
 * sono il modo in cui il marchio va in giro storto. Se `brand/` cambia, questo
 * file si aggiorna copiando i tracciati — e la prova sul marchio se ne accorge
 * se divergono.
 *
 * I tracciati stanno in costanti condivise fra le varianti: la versione a
 * gradiente e quella a colore unico devono restare **lo stesso disegno**, e
 * due copie della stessa stringa sono due occasioni di divergere.
 */

/** La D con l'onda dentro, come in `discovery-guard-mark.svg`. */
const TRACCIATO_D =
  'M 88 64 H 232 A 192 192 0 0 1 232 448 H 88 Z M 176 152 H 232 A 104 104 0 0 1 232 360 H 176 Z'
const ARCO_VICINO = 'M 262 316 A 48 48 0 0 0 214 268'
const ARCO_LONTANO = 'M 302 316 A 88 88 0 0 0 214 228'

/** La D con l'onda in `currentColor`: chi la usa decide il colore. */
export function OndaD({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" fill="none" aria-hidden="true">
      <path fill="currentColor" fillRule="evenodd" d={TRACCIATO_D} />
      <circle cx="214" cy="316" r="15" fill="currentColor" />
      <g fill="none" stroke="currentColor" strokeWidth="20" strokeLinecap="round">
        <path d={ARCO_VICINO} />
        <path d={ARCO_LONTANO} />
      </g>
    </svg>
  )
}

/** La versione principale: la D a gradiente, il segnale rosso pieno. */
export function OndaDGradiente({ size = 64 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="marchio-dg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8B0000" />
          <stop offset="1" stopColor="#E10600" />
        </linearGradient>
      </defs>
      <path fill="url(#marchio-dg)" fillRule="evenodd" d={TRACCIATO_D} />
      <circle cx="214" cy="316" r="15" fill="#E10600" />
      <g fill="none" stroke="#E10600" strokeWidth="20" strokeLinecap="round">
        <path d={ARCO_VICINO} />
        <path d={ARCO_LONTANO} />
      </g>
    </svg>
  )
}

/**
 * Il logo completo: marchio, nome, payoff — come sulla tavola dell'identità.
 *
 * Il nome è testo vivo in Poppins, non tracciati: il carattere lo serviamo
 * noi, quindi il testo resta nitido a ogni dimensione e leggibile agli screen
 * reader. Nome e payoff sono la marca, non interfaccia: NON passano da `t()`,
 * perché un logo non si traduce.
 */
export function LogoCompleto({ size = 64 }: { size?: number }) {
  return (
    <div className="logo-completo" role="img" aria-label="Discovery Guard — Discover today. A safer tomorrow.">
      <OndaDGradiente size={size} />
      <div className="logo-completo__nome" aria-hidden="true">
        {'Discovery'} <span>{'Guard'}</span>
      </div>
      <div className="logo-completo__payoff" aria-hidden="true">
        {'Discover today. A safer tomorrow.'}
      </div>
    </div>
  )
}

/** Il quadrotto: bianco su rosso, il raggio al 22.5% come l'icona ufficiale. */
export default function Marchio({ size = 22 }: { size?: number }) {
  return (
    <span className="marchio" style={{ width: size, height: size }} aria-hidden="true">
      <OndaD size={Math.round(size * 0.68)} />
    </span>
  )
}
